import TwitchEventSubService from '../../services/TwitchEventSubService';
import { ConnectionState } from './types';
import { type ParsedMessage, type ChatBadge } from './MessageParser';

export interface EventSubChatServiceEvents {
  onMessage: (message: ParsedMessage) => void;
  onConnectionStateChange: (state: ConnectionState) => void;
  onError: (error: Error) => void;
}

export interface EventSubConnectionStatus {
  state: ConnectionState;
  lastMessageTime: number;
  isEventSub: true;
  error?: string;
}

export class EventSubChatService {
  private eventSubService: TwitchEventSubService;
  private currentChannel: string | null = null;
  private userBadges: ChatBadge[] = [];
  private messageHandlers: Set<(message: ParsedMessage) => void> = new Set();
  private stateChangeHandlers: Set<(state: ConnectionState) => void> = new Set();
  private errorHandlers: Set<(error: Error) => void> = new Set();
  private lastMessageTime = 0;
  private connectionState = false;
  
  constructor(private accessToken: string) {
    console.log('[EventSubChatService] Initializing EventSub-only chat service');
    this.eventSubService = new TwitchEventSubService();
    this.setupEventSubHandlers();
  }

  public async connect(): Promise<void> {
    console.log('[EventSubChatService] Connecting to EventSub...');
    try {
      await this.eventSubService.connect();
      console.log('[EventSubChatService] EventSub connection initiated');
    } catch (error) {
      console.error('[EventSubChatService] Failed to connect to EventSub:', error);
      this.notifyErrorHandlers(error as Error);
      throw error;
    }
  }

  public disconnect(): void {
    console.log('[EventSubChatService] Disconnecting from EventSub...');
    this.currentChannel = null;
    this.connectionState = false;
    this.notifyStateChange(ConnectionState.DISCONNECTED);
  }

  public async joinChannel(channel: string): Promise<void> {
    console.log(`[EventSubChatService] Joining channel: ${channel}`);
    // EventSub automatically listens to the authenticated user's channel
    // No explicit join needed, but we track the channel for compatibility
    const channelLower = channel.toLowerCase();
    this.currentChannel = channelLower;
    localStorage.setItem('current_channel', channelLower);
    
    // Fetch user badges for this channel
    await this.fetchUserBadges();
  }

  public leaveChannel(channel?: string): void {
    console.log(`[EventSubChatService] Leaving channel: ${channel || this.currentChannel}`);
    const channelToLeave = channel || this.currentChannel;
    if (channelToLeave === this.currentChannel) {
      this.currentChannel = null;
      localStorage.removeItem('current_channel');
    }
  }

  public async sendMessage(_channel: string, message: string): Promise<boolean> {
    console.log(`[EventSubChatService] Sending message via API: ${message}`);
    
    if (!this.accessToken) {
      console.error('[EventSubChatService] No access token available for sending message');
      return false;
    }

    try {
      const user = JSON.parse(localStorage.getItem('twitch_user') || '{}');
      if (!user.id) {
        throw new Error('User information not available');
      }

      const response = await fetch(`https://api.twitch.tv/helix/chat/messages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'Client-Id': import.meta.env.VITE_TWITCH_CLIENT_ID,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          broadcaster_id: user.id,
          sender_id: user.id,
          message: message
        })
      });

      if (!response.ok) {
        const errorData = await response.text();
        console.error('[EventSubChatService] Failed to send message:', response.status, errorData);
        return false;
      }

      console.log('[EventSubChatService] Message sent successfully via API');
      return true;
    } catch (error) {
      console.error('[EventSubChatService] Error sending message:', error);
      return false;
    }
  }

  public async sendAnnouncement(_channel: string, message: string): Promise<void> {
    console.log(`[EventSubChatService] Sending announcement via API: ${message}`);
    
    if (!this.accessToken) {
      throw new Error('No access token available for sending announcement');
    }

    try {
      const user = JSON.parse(localStorage.getItem('twitch_user') || '{}');
      if (!user.id) {
        throw new Error('User information not available');
      }

      const response = await fetch(`https://api.twitch.tv/helix/chat/announcements?broadcaster_id=${user.id}&moderator_id=${user.id}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'Client-Id': import.meta.env.VITE_TWITCH_CLIENT_ID,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          message: message,
          color: 'primary'
        })
      });

      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(`Failed to send announcement: ${response.status} - ${errorData}`);
      }

      console.log('[EventSubChatService] Announcement sent successfully via API');
    } catch (error) {
      console.error('[EventSubChatService] Error sending announcement:', error);
      throw error;
    }
  }

  // Event handling - clean interface
  public onMessage(handler: (message: ParsedMessage) => void): () => void {
    this.messageHandlers.add(handler);
    return () => this.messageHandlers.delete(handler);
  }

  public onConnectionStateChange(handler: (state: ConnectionState) => void): () => void {
    this.stateChangeHandlers.add(handler);
    return () => this.stateChangeHandlers.delete(handler);
  }

  public onError(handler: (error: Error) => void): () => void {
    this.errorHandlers.add(handler);
    return () => this.errorHandlers.delete(handler);
  }

  // Status and information
  public isConnected(): boolean {
    return this.connectionState;
  }

  public getConnectionStatus(): EventSubConnectionStatus {
    return {
      state: this.connectionState ? ConnectionState.CONNECTED : ConnectionState.DISCONNECTED,
      lastMessageTime: this.lastMessageTime,
      isEventSub: true,
      error: !this.connectionState ? 'EventSub connection lost' : undefined
    };
  }

  public getCurrentChannel(): string | null {
    return this.currentChannel;
  }

  public getUserBadges(): ChatBadge[] {
    return [...this.userBadges];
  }

  // Force health check (for compatibility)
  public forceHealthCheck(): void {
    console.log('[EventSubChatService] Health check - EventSub only system');
    const status = this.getConnectionStatus();
    console.log('[EventSubChatService] Status:', status);
  }

  // Private methods
  private setupEventSubHandlers(): void {
    // Handle chat messages from EventSub
    this.eventSubService.onChatMessage((message: ParsedMessage) => {
      console.log('[EventSubChatService] Message received:', message.username, message.message);
      this.lastMessageTime = Date.now();
      this.notifyMessageHandlers(message);
    });

    // Handle connection state changes
    this.eventSubService.onConnectionStateChange((connected: boolean) => {
      console.log('[EventSubChatService] EventSub connection state:', connected);
      this.connectionState = connected;
      const state = connected ? ConnectionState.CONNECTED : ConnectionState.DISCONNECTED;
      this.notifyStateChange(state);
    });
  }

  private notifyStateChange(state: ConnectionState): void {
    console.log(`[EventSubChatService] Connection state changed to: ${state}`);
    this.stateChangeHandlers.forEach(handler => {
      try {
        handler(state);
      } catch (error) {
        console.error('[EventSubChatService] Error in state change handler:', error);
      }
    });
  }

  private notifyMessageHandlers(message: ParsedMessage): void {
    this.messageHandlers.forEach(handler => {
      try {
        handler(message);
      } catch (error) {
        console.error('[EventSubChatService] Error in message handler:', error);
      }
    });
  }

  private notifyErrorHandlers(error: Error): void {
    this.errorHandlers.forEach(handler => {
      try {
        handler(error);
      } catch (error) {
        console.error('[EventSubChatService] Error in error handler:', error);
      }
    });
  }

  private async fetchUserBadges(): Promise<void> {
    const user = JSON.parse(localStorage.getItem('twitch_user') || '{}');
    
    if (!user.id || !user.login || !this.currentChannel) {
      return;
    }

    try {
      const badges: ChatBadge[] = [];
      
      // Check if user is broadcaster
      if (this.currentChannel.toLowerCase() === user.login.toLowerCase()) {
        badges.push({ id: 'broadcaster', version: '1' });
      }

      // Get channel ID for API calls
      const channelResponse = await fetch(`https://api.twitch.tv/helix/users?login=${this.currentChannel}`, {
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'Client-Id': import.meta.env.VITE_TWITCH_CLIENT_ID
        }
      });

      if (!channelResponse.ok) {
        console.warn('[EventSubChatService] Failed to fetch channel info for badges');
        this.userBadges = badges;
        return;
      }

      const channelData = await channelResponse.json();
      const broadcasterId = channelData.data[0]?.id;
      if (!broadcasterId) {
        this.userBadges = badges;
        return;
      }

      // API headers for subsequent requests
      const apiHeaders = {
        'Authorization': `Bearer ${this.accessToken}`,
        'Client-Id': import.meta.env.VITE_TWITCH_CLIENT_ID
      };

      // Fetch subscription status and user data in parallel
      const [subResponse, userResponse] = await Promise.all([
        fetch(
          `https://api.twitch.tv/helix/subscriptions/user?broadcaster_id=${broadcasterId}&user_id=${user.id}`,
          { headers: apiHeaders }
        ).then(res => res.ok ? res.json() : null),
        
        fetch(
          `https://api.twitch.tv/helix/users?id=${user.id}`,
          { headers: apiHeaders }
        ).then(res => res.ok ? res.json() : null)
      ]);

      // Add subscription badge if applicable
      if (subResponse?.data?.[0]) {
        badges.push({ id: 'subscriber', version: subResponse.data[0].tier || '1' });
      }

      // Add partner badge if applicable  
      if (userResponse?.data?.[0]?.broadcaster_type === 'partner') {
        badges.push({ id: 'partner', version: '1' });
      }

      // TODO: Add more badge logic (VIP, moderator, etc.) as needed

      this.userBadges = badges;
      console.log('[EventSubChatService] User badges loaded:', badges);
    } catch (error) {
      console.error('[EventSubChatService] Error fetching user badges:', error);
      this.userBadges = [];
    }
  }
} 